import React, { useContext, useMemo, useState } from "react"
import { createRoot } from "react-dom/client"
import useProgress from "@kit/design/use-progress"
import { ReactRB } from "./api"
import { history } from "./history"
import { request } from "./request"

export const ReactRBNavigationContext = React.createContext({})
export const ReactRBPagePropsContext = React.createContext({})

const track = (page, attributes) => {
  attributes.account_id = window?.app?.user?.account_id

  if (window.analytics) {
    window.analytics.page(page, attributes)
  }
}

const DefaultLayout = ({ children }) => children

/**
 * Determine if ReactRB is enabled via local storage. This ability exists to
 * be able to test ReactRB in production without affecting all users. It will
 * eventually be removed.
 */
const localEnabled = () => {
  try {
    return JSON.parse(localStorage.getItem("react-rb:enabled"))
  } catch (e) {
    return false
  }
}

const config = {
  loadPageComponent: null
}

const toQueryString = object =>
  Object.keys(object)
    .map(key => key + "=" + object[key])
    .join("&")

export function useNavigate() {
  return useContext(ReactRBNavigationContext)
}

export function usePageProps() {
  return useContext(ReactRBPagePropsContext)
}

export const Link = React.forwardRef(
  (
    {
      to,
      children,
      as: Component = "a",
      page,
      preload = false,
      enabled = false,
      onClick = () => {},
      onMouseEnter,
      onFocus,
      ...props
    },
    ref
  ) => {
    const handleNavigate = event => {
      if (event.shiftKey || event.metaKey || event.ctrlKey) return
      if (!enabled) return

      event.preventDefault()
      onClick(event)
      ReactRB.navigate.go(to, { enabled: true })
    }

    const handleMouseEnter = event => {
      onMouseEnter?.(event)
      if (page && preload) config.loadPageComponent(page)
    }

    const handleFocus = event => {
      onFocus?.(event)
      if (page && preload) config.loadPageComponent(page)
    }

    return (
      <Component
        ref={ref}
        href={to}
        onClick={handleNavigate}
        onMouseEnter={handleMouseEnter}
        onFocus={handleFocus}
        {...props}
      >
        {children}
      </Component>
    )
  }
)

const getURL = () => `${window.location.pathname}${window.location.search}`

export { ReactRB }

function Page({ initialPage, initialState, layout: Layout }) {
  const [page, setPage] = useState(initialPage)
  const [state, setState] = useState(initialState)

  const progress = useProgress()

  const PageComponent = useMemo(
    () => React.lazy(() => config.loadPageComponent(page)),
    [page]
  )

  const go = async (path, { params, enabled } = {}) => {
    const url = [path, params ? toQueryString(params) : null]
      .filter(Boolean)
      .join("?")

    if (!enabled && !localEnabled()) {
      window.location = url
      return
    }

    progress.go(20)

    const { data: result } = await request(url)
    await history.push(url, { page: result.page, pageState: result.props })

    setPage(result.page)
    setState(result.props)

    track(result.title, {
      title: result.title,
      route: window.location.pathname.replace(/\d+/g, ":id")
    })

    document.title = result.title
    progress.finish()
  }

  const replace = async (path, { params, enabled } = {}) => {
    const url = [path, params ? toQueryString(params) : null]
      .filter(Boolean)
      .join("?")

    if (!enabled && !localEnabled()) {
      window.location = url
      return
    }

    progress.go(20)

    const { data: result } = await request(url)
    await history.replace(url, { page: result.page, pageState: result.props })

    setPage(result.page)
    setState(result.props)

    track(result.title, {
      title: result.title,
      route: window.location.pathname.replace(/\d+/g, ":id")
    })

    document.title = result.title
    progress.finish()
  }

  const refresh = async ({ only } = {}) => {
    const { data: result } = await request(getURL(), {
      headers: only ? { "X-React-RB-Only": only } : {}
    })

    setState({ ...state, ...result.props })
  }

  const navigate = {
    go,
    replace,
    refresh
  }

  ReactRB.navigate = navigate

  React.useEffect(() => {
    async function replaceHistory() {
      // On the initial page load, replace the current history entry with one
      // that contains our page state.
      await history.replace(getURL(), {
        page: initialPage,
        pageState: initialState
      })
    }
    replaceHistory()
  }, [initialPage, initialState])

  React.useEffect(() => {
    const onPopState = async event => {
      // History entry is not from ReactRB, so ignore it
      if (!event.state) return
      if (event.state.ReactRB) {
        // History entry does not contain state, likely because of it
        // being too large. Load the page as if the user had navigated
        // to it instead.
        const { data: result } = await request(getURL())

        setPage(result.page)
        setState(result.props)
        document.title = result.title
      } else {
        // History entry contains the state
        const { page, pageState } = event.state

        setPage(page || initialPage)
        setState(pageState || initialState)

        await history.replace(getURL(), { page, pageState })
      }
    }

    window.addEventListener("popstate", onPopState)

    return () => {
      window.removeEventListener("popstate", onPopState)
    }
  }, [])

  return (
    <ReactRBNavigationContext.Provider value={navigate}>
      <ReactRBPagePropsContext.Provider value={state}>
        <Layout>
          <PageComponent {...state} />
        </Layout>
      </ReactRBPagePropsContext.Provider>
    </ReactRBNavigationContext.Provider>
  )
}

export function configure({ loadPageComponent }) {
  config.loadPageComponent = loadPageComponent
}

export function initialize({ layout = DefaultLayout }) {
  const dataNode = document.querySelector("[data-component='Page']")
  if (!dataNode) return

  const props = JSON.parse(dataNode.dataset.props)
  const page = dataNode.dataset.page

  // Vertical nav: React mounts at .content_wrapper so it can render nav + banners
  // as siblings of the page. Standard layout: mount on the page node itself.
  const verticalNav = window.app?.layoutConfig?.components?.verticalNav?.visible
  const mountNode = verticalNav ? dataNode.closest(".content_wrapper") : dataNode
  if (verticalNav) dataNode.remove()

  const root = createRoot(mountNode)
  root.render(<Page initialPage={page} initialState={props} layout={layout} />)
}
